import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DynamicPage, DynamicPageTitle, Title, Bar, Button, Select, Option, Input, Label, CheckBox,
  Table, TableHeaderRow, TableHeaderCell, TableRow, TableCell,
  MessageStrip, Text, BusyIndicator, FlexBox, ObjectStatus, TabContainer, Tab, Panel,
} from '@ui5/webcomponents-react';
import {
  api, type ConsAccount, type ConsAccountType, type ConsGroupDetail, type ConsMember, type ConsMethod,
  type ConsSourceAccount,
} from '../api/client';
import { useIsMobile } from '../hooks/useIsMobile';

const money = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const METHODS: ConsMethod[] = ['full', 'proportionate', 'equity'];
const ACCOUNT_TYPES: ConsAccountType[] = ['asset', 'liability', 'equity', 'revenue', 'expense'];
const METHOD_HELP: Record<ConsMethod, string> = {
  full: '100% of the balances, with the share you do not own split out as non-controlling interest',
  proportionate: 'only your share of each balance',
  equity: 'a single investment line — no balances are brought in',
};

/**
 * Financial consolidation. The group is a presentation structure with no legal entity behind it, so it is
 * defined here rather than in SAP, and its members can be companies or other groups nested without limit.
 */
export function ConsolidationPage() {
  const [sp, setSp] = useSearchParams();
  const isMobile = useIsMobile();
  const qc = useQueryClient();
  const groupId = sp.get('group') ?? '';
  const tab = sp.get('tab') ?? 'structure';
  const setParam = (k: string, v: string) => setSp(p => { const n = new URLSearchParams(p); v ? n.set(k, v) : n.delete(k); return n; }, { replace: true });

  const [msg, setMsg] = useState<{ design: 'Positive' | 'Negative' | 'Information' | 'Critical'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const say = (design: 'Positive' | 'Negative' | 'Information' | 'Critical', text: string) => setMsg({ design, text });
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setMsg(null);
    try { await fn(); } catch (e) { say('Negative', (e as Error).message); } finally { setBusy(false); }
  };

  const groups = useQuery({ queryKey: ['cons-groups'], queryFn: api.consGroups });
  const detail = useQuery({ queryKey: ['cons-group', groupId], queryFn: () => api.consGroup(groupId), enabled: !!groupId });
  const d = detail.data;

  const refresh = async () => { await qc.invalidateQueries({ queryKey: ['cons-groups'] }); await detail.refetch(); };

  const actions = <>
    <Button icon="refresh" disabled={busy || !groupId} onClick={() => run(async () => {
      const r = await api.consSync(groupId);
      const failed = r.companies.filter(c => c.error);
      const scanned = r.companies.reduce((a, c) => a + c.scanned, 0);
      say(failed.length ? 'Critical' : 'Positive',
        failed.length
          ? `Synced ${scanned} entries; ${failed.length} member(s) failed: ${failed.map(f => `${f.company} (${f.error})`).join('; ')}`
          : `Members up to date — ${scanned} new journal entr${scanned === 1 ? 'y' : 'ies'} folded in.${r.more ? ' More remain: sync again.' : ''}`);
      await refresh();
    })}>{busy ? 'Working…' : 'Sync members'}</Button>
    <Button design="Emphasized" icon="add" disabled={busy} onClick={() => run(async () => {
      const id = window.prompt('New group code (short, e.g. FTGROUP):', '');
      if (!id?.trim()) return;
      const name = window.prompt('Group name:', id.trim()) ?? id.trim();
      const currency = window.prompt('Reporting currency (e.g. MYR, GBP):', '') ?? '';
      await api.saveConsGroup(id.trim(), { name, currency: currency.trim() });
      setParam('group', id.trim());
      say('Positive', `Group ${id.trim()} created. Add its members, then map their accounts.`);
      await refresh();
    })}>New group</Button>
  </>;

  return (
    <DynamicPage
      titleArea={<DynamicPageTitle
        heading={<Title level="H3">Consolidation</Title>}
        actionsBar={isMobile ? undefined : <Bar design="Subheader" endContent={actions} />} />}
      showFooter={isMobile} footerArea={isMobile ? <Bar design="Footer" endContent={actions} /> : undefined}>

      {msg && <MessageStrip design={msg.design} onClose={() => setMsg(null)} style={{ marginBottom: '0.5rem' }}>{msg.text}</MessageStrip>}

      <MessageStrip design="Information" hideCloseButton style={{ marginBottom: '0.75rem' }}>
        A group is a presentation structure — there is no legal entity and no SAP company behind it, so it is
        defined here and can be rearranged without touching any company's data. Members can be companies or
        other groups, nested as deeply as the structure needs; ownership compounds down the levels.
      </MessageStrip>

      <FlexBox wrap="Wrap" style={{ gap: '0.75rem', marginBottom: '0.75rem', alignItems: 'flex-end' }}>
        <FlexBox direction="Column" style={{ minWidth: '16rem' }}>
          <Label>Group</Label>
          <Select onChange={e => setParam('group', String(e.detail.selectedOption.dataset.key ?? ''))}>
            <Option data-key="">— choose —</Option>
            {(groups.data ?? []).map(g => (
              <Option key={g.id} data-key={g.id} selected={g.id === groupId}>
                {g.name} ({g.id}{g.currency ? ` · ${g.currency}` : ''}{g.members ? ` · ${g.members} member(s)` : ''})
              </Option>
            ))}
          </Select>
        </FlexBox>
        {groupId && (
          <Button icon="delete" design="Transparent" disabled={busy} onClick={() => run(async () => {
            if (!window.confirm(`Delete group ${groupId}? Its members, accounts, mappings and journals go with it. No SAP company data is touched.`)) return;
            await api.deleteConsGroup(groupId);
            setParam('group', '');
            say('Positive', `Group ${groupId} deleted.`);
            await refresh();
          })}>Delete group</Button>
        )}
      </FlexBox>

      {!groupId && <MessageStrip design="Information" hideCloseButton>Choose a group, or create one to begin.</MessageStrip>}
      {groupId && detail.isLoading && <BusyIndicator active style={{ margin: '2rem auto', display: 'block' }} />}
      {groupId && detail.isError && <MessageStrip design="Negative" hideCloseButton>{(detail.error as Error).message}</MessageStrip>}

      {d && (
        <>
          {d.warnings?.map((w, i) => (
            <MessageStrip key={i} design="Critical" hideCloseButton style={{ marginBottom: '0.4rem' }}>{w}</MessageStrip>
          ))}

          <TabContainer collapsed onTabSelect={e => setParam('tab', String((e.detail.tab as HTMLElement).dataset.key ?? 'structure'))}>
            <Tab data-key="structure" text="Structure" selected={tab === 'structure'}>
              <Structure detail={d} busy={busy} run={run} say={say} refresh={refresh} />
            </Tab>
            <Tab data-key="accounts" text="Group accounts" selected={tab === 'accounts'}>
              <GroupAccounts groupId={groupId} accounts={d.accounts} busy={busy} run={run} say={say} refresh={refresh} />
            </Tab>
            <Tab data-key="mapping" text="Account mapping" selected={tab === 'mapping'}>
              <Mapping groupId={groupId} detail={d} busy={busy} run={run} say={say} />
            </Tab>
            <Tab data-key="report" text="Trial balance" selected={tab === 'report'}>
              <TrialBalance groupId={groupId} />
            </Tab>
          </TabContainer>
        </>
      )}
    </DynamicPage>
  );
}

// ---------------------------------------------------------------- structure
function Structure({ detail, busy, run, say, refresh }: {
  detail: ConsGroupDetail;
  busy: boolean;
  run: (fn: () => Promise<void>) => Promise<void>;
  say: (d: 'Positive' | 'Negative' | 'Information' | 'Critical', t: string) => void;
  refresh: () => Promise<void>;
}) {
  const g = detail.group;
  const [draft, setDraft] = useState({ name: g.name, currency: g.currency, ctaAccount: g.ctaAccount, note: g.note ?? '' });
  useEffect(() => setDraft({ name: g.name, currency: g.currency, ctaAccount: g.ctaAccount, note: g.note ?? '' }), [g]);
  const companies = useQuery({ queryKey: ['auth-companies'], queryFn: api.companies });

  return (
    <div style={{ paddingTop: '0.75rem' }}>
      <Panel headerText="Group settings" collapsed>
        <FlexBox wrap="Wrap" style={{ gap: '0.75rem', padding: '0.5rem 0' }}>
          <Field label="Name" value={draft.name} onChange={v => setDraft(s => ({ ...s, name: v }))} />
          <Field label="Reporting currency" value={draft.currency} onChange={v => setDraft(s => ({ ...s, currency: v }))} placeholder="GBP" />
          <Field label="Translation reserve account" value={draft.ctaAccount} onChange={v => setDraft(s => ({ ...s, ctaAccount: v }))} placeholder="G-CTA" />
          <Field label="Note" value={draft.note} onChange={v => setDraft(s => ({ ...s, note: v }))} width="20rem" />
          <FlexBox alignItems="End">
            <Button design="Emphasized" disabled={busy} onClick={() => run(async () => {
              await api.saveConsGroup(g.id, draft);
              say('Positive', 'Group settings saved.');
              await refresh();
            })}>Save</Button>
          </FlexBox>
        </FlexBox>
        <Text style={{ fontSize: '0.8rem', color: 'var(--sapNeutralTextColor)' }}>
          The translation reserve absorbs the currency translation adjustment. Translating the balance sheet at
          the closing rate and the P&amp;L at the average rate cannot balance; without a reserve nominated, that
          difference is reported but left unallocated.
        </Text>
      </Panel>

      <Title level="H5" style={{ margin: '1rem 0 0.4rem' }}>Members</Title>
      <Table>
        <TableHeaderRow slot="headerRow">
          <TableHeaderCell><span>Member</span></TableHeaderCell>
          <TableHeaderCell><span>Kind</span></TableHeaderCell>
          <TableHeaderCell horizontalAlign="End"><span>Owned %</span></TableHeaderCell>
          <TableHeaderCell><span>Method</span></TableHeaderCell>
          <TableHeaderCell><span>Currency</span></TableHeaderCell>
          <TableHeaderCell><span></span></TableHeaderCell>
        </TableHeaderRow>
        {detail.members.map(m => (
          <TableRow key={m.memberId}>
            <TableCell><Text>{m.memberId}</Text></TableCell>
            <TableCell><Text>{m.kind === 'group' ? 'sub-group' : 'company'}</Text></TableCell>
            <TableCell><Text style={{ width: '100%', textAlign: 'right' }}>{m.ownershipPct}%</Text></TableCell>
            <TableCell><Text>{m.method}</Text></TableCell>
            <TableCell><Text>{m.currency ?? '—'}</Text></TableCell>
            <TableCell>
              <Button icon="decline" design="Transparent" disabled={busy} onClick={() => run(async () => {
                await api.deleteConsMember(g.id, m.memberId);
                say('Positive', `${m.memberId} removed.`);
                await refresh();
              })} />
            </TableCell>
          </TableRow>
        ))}
      </Table>
      {!detail.members.length && (
        <MessageStrip design="Information" hideCloseButton style={{ marginTop: '0.5rem' }}>
          No members yet. Add the holding company first, then its subsidiaries.
        </MessageStrip>
      )}

      <AddMember groupId={g.id} companies={companies.data?.companies ?? []} busy={busy} run={run} say={say} refresh={refresh} />

      {detail.tree.length > 0 && (
        <>
          <Title level="H5" style={{ margin: '1.25rem 0 0.4rem' }}>Resolved structure</Title>
          <Text style={{ display: 'block', fontSize: '0.8rem', color: 'var(--sapNeutralTextColor)', marginBottom: '0.4rem' }}>
            Effective ownership compounds down the levels — a 60% holding inside an 80%-owned subsidiary is 48%
            to the group, leaving 52% as non-controlling interest.
          </Text>
          <Table>
            <TableHeaderRow slot="headerRow">
              <TableHeaderCell><span>Member</span></TableHeaderCell>
              <TableHeaderCell horizontalAlign="End"><span>Direct %</span></TableHeaderCell>
              <TableHeaderCell horizontalAlign="End"><span>Effective %</span></TableHeaderCell>
              <TableHeaderCell horizontalAlign="End"><span>NCI %</span></TableHeaderCell>
              <TableHeaderCell><span>Treatment</span></TableHeaderCell>
            </TableHeaderRow>
            {detail.tree.map((t, i) => (
              <TableRow key={`${t.memberId}-${i}`}>
                <TableCell>
                  <Text style={{ paddingLeft: `${Math.max(0, t.path.length - 1) * 1.2}rem` }}>
                    {t.path.length > 1 ? '↳ ' : ''}{t.memberId}
                  </Text>
                </TableCell>
                <TableCell><Text style={{ width: '100%', textAlign: 'right' }}>{t.directPct}%</Text></TableCell>
                <TableCell><Text style={{ width: '100%', textAlign: 'right' }}>{t.effectivePct}%</Text></TableCell>
                <TableCell>
                  <Text style={{ width: '100%', textAlign: 'right' }}>
                    {t.method === 'full' ? `${Math.round((100 - t.effectivePct) * 100) / 100}%` : '—'}
                  </Text>
                </TableCell>
                <TableCell>
                  <ObjectStatus state={t.equityCarried ? 'Information' : 'Positive'}>
                    {t.equityCarried ? 'equity — single line' : METHOD_HELP[t.method].split(',')[0]}
                  </ObjectStatus>
                </TableCell>
              </TableRow>
            ))}
          </Table>
        </>
      )}
    </div>
  );
}

function AddMember({ groupId, companies, busy, run, say, refresh }: {
  groupId: string; companies: string[]; busy: boolean;
  run: (fn: () => Promise<void>) => Promise<void>;
  say: (d: 'Positive' | 'Negative' | 'Information' | 'Critical', t: string) => void;
  refresh: () => Promise<void>;
}) {
  const [m, setM] = useState<ConsMember>({ memberId: '', kind: 'company', ownershipPct: 100, method: 'full', currency: '' });
  return (
    <Panel headerText="Add a member" collapsed style={{ marginTop: '0.75rem' }}>
      <FlexBox wrap="Wrap" style={{ gap: '0.75rem', padding: '0.5rem 0', alignItems: 'flex-end' }}>
        <FlexBox direction="Column" style={{ minWidth: '13rem' }}>
          <Label>Company</Label>
          <Select onChange={e => setM(s => ({ ...s, memberId: String(e.detail.selectedOption.dataset.key ?? '') }))}>
            <Option data-key="">— choose —</Option>
            {companies.map(c => <Option key={c} data-key={c} selected={c === m.memberId}>{c}</Option>)}
          </Select>
        </FlexBox>
        <FlexBox direction="Column" style={{ width: '8rem' }}>
          <Label>Owned %</Label>
          <Input type="Number" value={String(m.ownershipPct)} onChange={e => setM(s => ({ ...s, ownershipPct: Number(e.target.value ?? 100) }))} />
        </FlexBox>
        <FlexBox direction="Column" style={{ minWidth: '12rem' }}>
          <Label>Method</Label>
          <Select onChange={e => setM(s => ({ ...s, method: String(e.detail.selectedOption.dataset.key ?? 'full') as ConsMethod }))}>
            {METHODS.map(x => <Option key={x} data-key={x} selected={x === m.method}>{x}</Option>)}
          </Select>
        </FlexBox>
        <FlexBox direction="Column" style={{ width: '8rem' }}>
          <Label>Currency</Label>
          <Input value={m.currency ?? ''} placeholder="auto" onChange={e => setM(s => ({ ...s, currency: e.target.value ?? '' }))} />
        </FlexBox>
        <Button design="Emphasized" disabled={busy || !m.memberId} onClick={() => run(async () => {
          const r = await api.saveConsMember(groupId, m);
          say('Positive', `${m.memberId} added at ${m.ownershipPct}% (${m.method}).${r.warnings?.length ? ' ' + r.warnings.join(' ') : ''}`);
          setM({ memberId: '', kind: 'company', ownershipPct: 100, method: 'full', currency: '' });
          await refresh();
        })}>Add</Button>
      </FlexBox>
      <Text style={{ fontSize: '0.8rem', color: 'var(--sapNeutralTextColor)' }}>{METHOD_HELP[m.method]}. Leave currency blank to derive it from SAP.</Text>
    </Panel>
  );
}

// ---------------------------------------------------------------- group chart of accounts
function GroupAccounts({ groupId, accounts, busy, run, say, refresh }: {
  groupId: string; accounts: ConsAccount[]; busy: boolean;
  run: (fn: () => Promise<void>) => Promise<void>;
  say: (d: 'Positive' | 'Negative' | 'Information' | 'Critical', t: string) => void;
  refresh: () => Promise<void>;
}) {
  const [rows, setRows] = useState<ConsAccount[]>(accounts);
  useEffect(() => setRows(accounts), [accounts]);
  const set = (i: number, patch: Partial<ConsAccount>) => setRows(r => r.map((x, j) => j === i ? { ...x, ...patch } : x));

  return (
    <div style={{ paddingTop: '0.75rem' }}>
      <Text style={{ display: 'block', fontSize: '0.85rem', color: 'var(--sapNeutralTextColor)', marginBottom: '0.5rem' }}>
        The group's own chart of accounts. Member companies rarely share one — codes and even numbering
        conventions differ — so each company's accounts are mapped onto these. The type decides the exchange
        rate: balance-sheet accounts translate at the closing rate, revenue and expenses at the average.
      </Text>
      <Table>
        <TableHeaderRow slot="headerRow">
          <TableHeaderCell><span>Code</span></TableHeaderCell>
          <TableHeaderCell><span>Name</span></TableHeaderCell>
          <TableHeaderCell><span>Type</span></TableHeaderCell>
          <TableHeaderCell horizontalAlign="End"><span>Order</span></TableHeaderCell>
        </TableHeaderRow>
        {rows.map((a, i) => (
          <TableRow key={`${a.code}-${i}`}>
            <TableCell><Input value={a.code} onChange={e => set(i, { code: e.target.value ?? '' })} style={{ width: '100%' }} /></TableCell>
            <TableCell><Input value={a.name} onChange={e => set(i, { name: e.target.value ?? '' })} style={{ width: '100%' }} /></TableCell>
            <TableCell>
              <Select onChange={e => set(i, { type: String(e.detail.selectedOption.dataset.key ?? 'asset') as ConsAccountType })}>
                {ACCOUNT_TYPES.map(t => <Option key={t} data-key={t} selected={t === a.type}>{t}</Option>)}
              </Select>
            </TableCell>
            <TableCell><Input type="Number" value={String(a.seq ?? 0)} onChange={e => set(i, { seq: Number(e.target.value ?? 0) })} style={{ width: '5rem' }} /></TableCell>
          </TableRow>
        ))}
      </Table>
      <FlexBox style={{ gap: '0.5rem', marginTop: '0.6rem' }}>
        <Button icon="add" onClick={() => setRows(r => [...r, { code: '', name: '', type: 'asset', seq: (r.length + 1) * 10 }])}>Add account</Button>
        <Button icon="template" onClick={() => setRows([
          { code: 'G-ASSET', name: 'Assets', type: 'asset', seq: 10 },
          { code: 'G-LIAB', name: 'Liabilities', type: 'liability', seq: 20 },
          { code: 'G-EQ', name: 'Equity', type: 'equity', seq: 30 },
          { code: 'G-CTA', name: 'Translation reserve', type: 'equity', seq: 40 },
          { code: 'G-REV', name: 'Revenue', type: 'revenue', seq: 50 },
          { code: 'G-EXP', name: 'Expenses', type: 'expense', seq: 60 },
        ])}>Use a starter chart</Button>
        <Button design="Emphasized" disabled={busy} onClick={() => run(async () => {
          const clean = rows.filter(r => r.code.trim() && r.name.trim());
          await api.saveConsAccounts(groupId, clean);
          say('Positive', `${clean.length} group account(s) saved.`);
          await refresh();
        })}>Save accounts</Button>
      </FlexBox>
    </div>
  );
}

// ---------------------------------------------------------------- mapping
function Mapping({ groupId, detail, busy, run, say }: {
  groupId: string;
  detail: { members: ConsMember[]; accounts: ConsAccount[] };
  busy: boolean;
  run: (fn: () => Promise<void>) => Promise<void>;
  say: (d: 'Positive' | 'Negative' | 'Information' | 'Critical', t: string) => void;
}) {
  const companies = detail.members.filter(m => m.kind === 'company').map(m => m.memberId);
  const [source, setSource] = useState(companies[0] ?? '');
  const [onlyActive, setOnlyActive] = useState(true);
  const [edits, setEdits] = useState<Record<string, string>>({});
  useEffect(() => { setEdits({}); if (!source && companies.length) setSource(companies[0]); }, [source, companies.length]);

  const src = useQuery({
    queryKey: ['cons-source-accounts', groupId, source],
    queryFn: () => api.consSourceAccounts(groupId, source),
    enabled: !!source,
  });

  const shown = useMemo(() => {
    const all = src.data?.accounts ?? [];
    return onlyActive ? all.filter(a => a.hasActivity) : all;
  }, [src.data, onlyActive]);

  const valueFor = (a: ConsSourceAccount) => edits[a.code] ?? a.mapped;

  if (!companies.length) return <MessageStrip design="Information" hideCloseButton style={{ marginTop: '0.75rem' }}>Add member companies first.</MessageStrip>;

  return (
    <div style={{ paddingTop: '0.75rem' }}>
      <FlexBox wrap="Wrap" style={{ gap: '0.75rem', alignItems: 'flex-end', marginBottom: '0.6rem' }}>
        <FlexBox direction="Column" style={{ minWidth: '14rem' }}>
          <Label>Company</Label>
          <Select onChange={e => { setSource(String(e.detail.selectedOption.dataset.key ?? '')); setEdits({}); }}>
            {companies.map(c => <Option key={c} data-key={c} selected={c === source}>{c}</Option>)}
          </Select>
        </FlexBox>
        <CheckBox checked={onlyActive} onChange={e => setOnlyActive(!!e.target.checked)} text="Only accounts with postings" />
        <Button icon="wand" disabled={busy || !shown.length} onClick={() => {
          const next: Record<string, string> = { ...edits };
          let n = 0;
          for (const a of shown) if (!valueFor(a) && a.suggested) { next[a.code] = a.suggested; n++; }
          setEdits(next);
          say('Information', `${n} suggestion(s) applied — review them, then Save.`);
        }}>Apply suggestions</Button>
        <Button design="Emphasized" disabled={busy || !Object.keys(edits).length} onClick={() => run(async () => {
          const rows = Object.entries(edits).filter(([, v]) => v).map(([localAccount, groupAccount]) => ({ source, localAccount, groupAccount }));
          if (!rows.length) return;
          await api.saveConsMap(groupId, rows);
          say('Positive', `${rows.length} mapping(s) saved for ${source}.`);
          setEdits({});
          await src.refetch();
        })}>Save mapping ({Object.keys(edits).length})</Button>
      </FlexBox>

      {src.data && (
        <Text style={{ display: 'block', fontSize: '0.8rem', color: 'var(--sapNeutralTextColor)', marginBottom: '0.5rem' }}>
          {src.data.counts.total} accounts · {src.data.counts.withActivity} with postings · {src.data.counts.mapped} mapped ·{' '}
          <strong>{src.data.counts.unmappedWithActivity} with postings still unmapped</strong> — unmapped accounts are
          excluded from the group figures, so these are the ones that distort the report.
        </Text>
      )}

      {src.isLoading && <BusyIndicator active style={{ margin: '2rem auto', display: 'block' }} />}
      {src.isError && <MessageStrip design="Negative" hideCloseButton>{(src.error as Error).message}</MessageStrip>}

      {!!shown.length && (
        <Table>
          <TableHeaderRow slot="headerRow">
            <TableHeaderCell><span>Local account</span></TableHeaderCell>
            <TableHeaderCell><span>SAP type</span></TableHeaderCell>
            <TableHeaderCell horizontalAlign="End"><span>Net</span></TableHeaderCell>
            <TableHeaderCell><span>Group account</span></TableHeaderCell>
          </TableHeaderRow>
          {shown.map(a => (
            <TableRow key={a.code}>
              <TableCell>
                <FlexBox direction="Column">
                  <Text>{a.code}</Text>
                  <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)' }}>{a.name}</Text>
                </FlexBox>
              </TableCell>
              <TableCell>
                <FlexBox direction="Column">
                  <Text style={{ fontSize: '0.8rem' }}>{a.accountType.replace('at_', '')}</Text>
                  {a.suggested && !valueFor(a) && (
                    <ObjectStatus state={a.confident ? 'Positive' : 'Critical'} style={{ fontSize: '0.7rem' }}>
                      {a.confident ? `suggests ${a.suggested}` : `guess: ${a.suggested}`}
                    </ObjectStatus>
                  )}
                </FlexBox>
              </TableCell>
              <TableCell><Text style={{ width: '100%', textAlign: 'right' }}>{a.hasActivity ? money(a.net) : '—'}</Text></TableCell>
              <TableCell>
                <Select style={{ width: '100%' }} onChange={e => setEdits(s => ({ ...s, [a.code]: String(e.detail.selectedOption.dataset.key ?? '') }))}>
                  <Option data-key="" selected={!valueFor(a)}>— unmapped —</Option>
                  {detail.accounts.map(g => (
                    <Option key={g.code} data-key={g.code} selected={valueFor(a) === g.code}>{g.code} · {g.name}</Option>
                  ))}
                </Select>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      )}
      {src.data && !shown.length && (
        <MessageStrip design="Information" hideCloseButton>
          Nothing to show. {onlyActive ? 'No accounts carry postings — sync the members first.' : 'This company has no accounts.'}
        </MessageStrip>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- trial balance
function TrialBalance({ groupId }: { groupId: string }) {
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [from, setFrom] = useState(1);
  const [to, setTo] = useState(12);

  const r = useQuery({
    queryKey: ['cons-tb', groupId, year, from, to],
    queryFn: () => api.consTrialBalance(groupId, { year, fromPeriod: from, toPeriod: to }),
    enabled: !!groupId && /^\d{4}$/.test(year),
  });
  const d = r.data;
  const memberIds = useMemo(() => {
    const s = new Set<string>();
    for (const row of d?.rows ?? []) for (const k of Object.keys(row.byMember)) s.add(k);
    return [...s].sort();
  }, [d]);

  return (
    <div style={{ paddingTop: '0.75rem' }}>
      <FlexBox wrap="Wrap" style={{ gap: '0.75rem', alignItems: 'flex-end', marginBottom: '0.6rem' }}>
        <FlexBox direction="Column" style={{ width: '7rem' }}><Label>Year</Label>
          <Input value={year} onChange={e => setYear(e.target.value ?? '')} /></FlexBox>
        <FlexBox direction="Column" style={{ width: '7rem' }}><Label>From period</Label>
          <Input type="Number" value={String(from)} onChange={e => setFrom(Number(e.target.value ?? 1))} /></FlexBox>
        <FlexBox direction="Column" style={{ width: '7rem' }}><Label>To period</Label>
          <Input type="Number" value={String(to)} onChange={e => setTo(Number(e.target.value ?? 12))} /></FlexBox>
      </FlexBox>

      {r.isLoading && <BusyIndicator active style={{ margin: '2rem auto', display: 'block' }} />}
      {r.isError && <MessageStrip design="Negative" hideCloseButton>{(r.error as Error).message}</MessageStrip>}

      {d && (
        <>
          {d.warnings?.map((w, i) => <MessageStrip key={i} design="Critical" hideCloseButton style={{ marginBottom: '0.4rem' }}>{w}</MessageStrip>)}

          <Text style={{ display: 'block', fontSize: '0.8rem', color: 'var(--sapNeutralTextColor)', marginBottom: '0.5rem' }}>
            {d.group.name} · {d.group.currency || 'no reporting currency set'} · FY{d.year} periods {d.fromPeriod}–{d.toPeriod} ({d.window.from} to {d.window.to})
          </Text>

          <Table>
            <TableHeaderRow slot="headerRow">
              <TableHeaderCell><span>Account</span></TableHeaderCell>
              <TableHeaderCell><span>Type</span></TableHeaderCell>
              {memberIds.map(m => <TableHeaderCell key={m} horizontalAlign="End"><span>{m.replace('journal:', '')}</span></TableHeaderCell>)}
              <TableHeaderCell horizontalAlign="End"><span>Group</span></TableHeaderCell>
              <TableHeaderCell horizontalAlign="End"><span>NCI</span></TableHeaderCell>
            </TableHeaderRow>
            {d.rows.map(row => (
              <TableRow key={row.code}>
                <TableCell>
                  <FlexBox direction="Column">
                    <Text>{row.name}</Text>
                    <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)' }}>{row.code}</Text>
                  </FlexBox>
                </TableCell>
                <TableCell><Text style={{ fontSize: '0.8rem' }}>{row.type}</Text></TableCell>
                {memberIds.map(m => (
                  <TableCell key={m}><Text style={{ width: '100%', textAlign: 'right' }}>{row.byMember[m] !== undefined ? money(row.byMember[m]) : '—'}</Text></TableCell>
                ))}
                <TableCell><Text style={{ width: '100%', textAlign: 'right', fontWeight: 600 }}>{money(row.group)}</Text></TableCell>
                <TableCell><Text style={{ width: '100%', textAlign: 'right' }}>{row.nci ? money(row.nci) : '—'}</Text></TableCell>
              </TableRow>
            ))}
          </Table>

          <FlexBox wrap="Wrap" style={{ gap: '1.5rem', marginTop: '0.75rem' }}>
            <Summary label="Group total" value={money(d.totals.group)} state={Math.abs(d.totals.group) < 0.01 ? 'Positive' : 'Negative'} />
            <Summary label="Non-controlling interest" value={money(d.totals.nci)} />
            <Summary label="Eliminations / adjustments" value={money(d.totals.journalNet)} />
          </FlexBox>
          <Text style={{ display: 'block', fontSize: '0.8rem', color: 'var(--sapNeutralTextColor)', marginTop: '0.4rem' }}>
            A consolidated trial balance should total zero. Anything else means accounts are unmapped, or the
            translation difference has nowhere to go — nominate a translation reserve on the group.
          </Text>

          {!!d.unmapped.length && (
            <Panel headerText={`Excluded — unmapped accounts (${d.unmapped.length})`} collapsed style={{ marginTop: '0.75rem' }}>
              <Table>
                <TableHeaderRow slot="headerRow">
                  <TableHeaderCell><span>Company</span></TableHeaderCell>
                  <TableHeaderCell><span>Local account</span></TableHeaderCell>
                  <TableHeaderCell horizontalAlign="End"><span>Amount</span></TableHeaderCell>
                </TableHeaderRow>
                {d.unmapped.slice(0, 100).map((u, i) => (
                  <TableRow key={`${u.member}-${u.account}-${i}`}>
                    <TableCell><Text>{u.member}</Text></TableCell>
                    <TableCell><Text>{u.account}</Text></TableCell>
                    <TableCell><Text style={{ width: '100%', textAlign: 'right' }}>{money(u.amount)}</Text></TableCell>
                  </TableRow>
                ))}
              </Table>
            </Panel>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- small shared bits
function Field({ label, value, onChange, placeholder, width = '12rem' }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; width?: string;
}) {
  return (
    <FlexBox direction="Column" style={{ width }}>
      <Label>{label}</Label>
      <Input value={value} placeholder={placeholder} onChange={e => onChange(e.target.value ?? '')} style={{ width: '100%' }} />
    </FlexBox>
  );
}

function Summary({ label, value, state }: { label: string; value: string; state?: 'Positive' | 'Negative' | 'Critical' }) {
  return (
    <FlexBox direction="Column">
      <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)' }}>{label}</Text>
      {state ? <ObjectStatus state={state} style={{ fontSize: '1.25rem' }}>{value}</ObjectStatus>
        : <Text style={{ fontSize: '1.25rem' }}>{value}</Text>}
    </FlexBox>
  );
}
